// Per-browser preferences for the overview: muted subjects (with an expiry) and "my list", the
// subjects a user cares about. Both live in localStorage — no account, no server.
import { useCallback, useEffect, useState } from 'react';

const MUTES_KEY = 'sbly-tools:baselines:mutes';
const LIST_KEY = 'sbly-tools:baselines:my-list';

export type Mutes = Record<string, string>; // subject key → ISO instant the mute ends

/** How long a mute lasts; "today" ends at local midnight, the default. */
export const MUTE_OPTIONS: Array<{ key: string; label: string; until: () => Date | null }> = [
  { key: 'today', label: 'Rest of today', until: () => { const d = new Date(); d.setHours(24, 0, 0, 0); return d; } },
  { key: '3d', label: '3 days', until: () => { const d = new Date(); d.setDate(d.getDate() + 3); return d; } },
  { key: '1w', label: '1 week', until: () => { const d = new Date(); d.setDate(d.getDate() + 7); return d; } },
  { key: '1m', label: '1 month', until: () => { const d = new Date(); d.setMonth(d.getMonth() + 1); return d; } },
  { key: 'forever', label: 'Until I unmute', until: () => null },
];
const FOREVER = '9999-12-31T00:00:00.000Z';

function read<T>(key: string, fallback: T): T {
  try { const s = localStorage.getItem(key); return s ? (JSON.parse(s) as T) : fallback; } catch { return fallback; }
}
function write(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ }
}

/** Mutes still in force (expired ones are dropped on read). */
export function useMutes(): { mutes: Mutes; mute: (key: string, option: string) => void; unmute: (key: string) => void } {
  const [mutes, setMutes] = useState<Mutes>(() => prune(read<Mutes>(MUTES_KEY, {})));
  useEffect(() => { write(MUTES_KEY, mutes); }, [mutes]);
  const mute = useCallback((key: string, option: string) => {
    const o = MUTE_OPTIONS.find(x => x.key === option) ?? MUTE_OPTIONS[0];
    const until = o.until();
    setMutes(prev => ({ ...prev, [key]: until ? until.toISOString() : FOREVER }));
  }, []);
  const unmute = useCallback((key: string) => setMutes(prev => { const next = { ...prev }; delete next[key]; return next; }), []);
  return { mutes, mute, unmute };
}
function prune(m: Mutes): Mutes {
  const now = Date.now();
  return Object.fromEntries(Object.entries(m).filter(([, until]) => Date.parse(until) > now));
}
export function muteLabel(until: string): string {
  if (until === FOREVER) return 'until unmuted';
  const d = new Date(until);
  const today = new Date(); today.setHours(24, 0, 0, 0);
  if (d.getTime() <= today.getTime()) return 'rest of today';
  return `until ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

/** The subjects a user follows; `only` = show just those on the overview. */
export function useMyList(): { list: Set<string>; toggle: (key: string) => void; only: boolean; setOnly: (v: boolean) => void } {
  const [list, setList] = useState<Set<string>>(() => new Set(read<string[]>(LIST_KEY, [])));
  const [only, setOnlyState] = useState<boolean>(() => read<boolean>(`${LIST_KEY}:only`, false));
  useEffect(() => { write(LIST_KEY, Array.from(list)); }, [list]);
  const toggle = useCallback((key: string) => setList(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; }), []);
  const setOnly = useCallback((v: boolean) => { setOnlyState(v); write(`${LIST_KEY}:only`, v); }, []);
  return { list, toggle, only, setOnly };
}
